"use client";

import * as React from "react";
import { MultiLevelCombobox, GenericOption } from "./multi-level-combobox";

interface LaborTypeOption {
  key: string;
  name: string;
  children?: LaborTypeOption[];
}

interface LaborTypeComboboxProps {
  options: LaborTypeOption[];
  name?: string;
  width?: string;
  placeholder?: string;
  emptyMessage?: string;
  defaultCategoryValue?: string;
  defaultTypeValue?: string;
  onCategoryChange?: (value: string, label: string) => void;
  onTypeChange?: (value: string, label: string) => void;
  style?: React.CSSProperties;
  compact?: boolean;
}

export function LaborTypeCombobox({
  options,
  width = undefined,
  placeholder = "Select labor type...",
  emptyMessage = "No labor type found.",
  defaultCategoryValue = "",
  defaultTypeValue = "",
  onCategoryChange,
  onTypeChange,
  name = undefined,
  style = {},
  compact = false,
}: Readonly<LaborTypeComboboxProps>) {
  // Convert labor options to generic format
  const convertToGenericOptions = (opts: LaborTypeOption[]): GenericOption[] => {
    return opts.map(opt => ({
      value: opt.key,
      label: opt.name,
      children: opt.children ? convertToGenericOptions(opt.children) : undefined,
    }));
  };

  const genericOptions = React.useMemo(() => convertToGenericOptions(options), [options]);

  // Convert default values to array format
  const defaultValues = React.useMemo(() => {
    const values = [];
    if (defaultCategoryValue) values[0] = defaultCategoryValue;
    if (defaultTypeValue) values[1] = defaultTypeValue;
    return values;
  }, [defaultCategoryValue, defaultTypeValue]);

  // Handle selection changes
  const handleSelectionChange = React.useCallback((
    values: string[],
    labels: string[]
  ) => {
    // Level 0: Category
    if (values[0] && labels[0] && onCategoryChange) {
      onCategoryChange(values[0], labels[0]);
    }
    
    // Level 1: Type
    if (values[1] && labels[1] && onTypeChange) {
      onTypeChange(values[1], labels[1]);
    }
  }, [onCategoryChange, onTypeChange]);

  // Custom display format for labor types
  const displayFormat = React.useCallback((selection: GenericOption[]): string => {
    if (selection.length === 0) return '';
    
    // For labor types: show "Type (Category)" format
    if (selection.length === 2) {
      const [category, type] = selection;
      return `${type.label} (${category.label})`;
    }
    
    // For single selection, just show the label
    return selection[selection.length - 1].label;
  }, []);

  return (
    <MultiLevelCombobox
      options={genericOptions}
      name={name}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      defaultValues={defaultValues}
      onSelectionChange={handleSelectionChange}
      style={style}
      compact={compact}
      showPriceFilter={false}
      levelConfigs={[
        { placeholder: "Search category...", backButtonLabel: "Back to categories" },
        { placeholder: "Search type...", backButtonLabel: "Back to category" }
      ]}
      displayFormat={displayFormat}
    />
  );
} 